import React from 'react';
import { Typography, Box, Link } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { EsPlayButton } from '../EsButtons/EsPlayButton';
import { IProject } from '../../types/global/helper';
import { useEsHover } from '../Animation/useEsHover';
import { isEmpty } from '../../utils/lodash';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { SplitWreathe } from '../Wreathe/SplitWreathe';
import { AwardCup } from '../AwardCup/AwardCup';

export interface IProjectPlayer {
    project: IProject;
    total_votes?: number;
    awards?: any[];
    position?: number;
    onPlay?: (project: IProject) => void;
    height?: string | number;
    width?: string | number;
}

const ProjectPlayer: React.FC<IProjectPlayer> = ({
    project,
    total_votes,
    awards,
    position,
    onPlay,
    height = '245px',
    width = '100%',
}) => {
    const { themeStyle } = useThemeContext();
    const { handleMouseEnter, handleMouseLeave, isHover } = useEsHover();

    // Check if there is at least one award
    const hasAward = !isEmpty(awards) && awards && awards.length > 0;
    // Get the first award
    const award = hasAward ? awards[0] : null;

    // Check if project has completed processing and is ready to play
    const shouldEnablePlayerProcessingState =
        project && project.processing_complete && project.project_type !== 'picture';

    return (
        <Link
            sx={{
                width,
                height,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                borderRadius: '10px',
                border: 'none',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                textDecoration: 'none',
                backgroundColor: theme[themeStyle].card_grey,
                overflow: 'clip',
                cursor: 'pointer',
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}>
            {/* Gradient overlay for award badges */}
            {hasAward && award && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '70%',
                        height: '150px',
                        background:
                            'radial-gradient(circle at top right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0) 100%)',
                        borderTopRightRadius: '10px',
                        zIndex: 5,
                    }}
                />
            )}

            {/* Award Cup - Position based */}
            {position && position <= 5 && (
                <Box sx={{ position: 'absolute', top: 10, left: 10, zIndex: 10 }}>
                    <AwardCup position={position} size="large" />
                </Box>
            )}

            {/* Award Badge */}
            {hasAward && award && (
                <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}>
                    <SplitWreathe
                        textLine1={award.award_status}
                        textLine2={award.award_category.category_title}
                        textLine3=""
                    />
                </Box>
            )}

            {/* Vote Count */}
            {!!total_votes && total_votes > 0 && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 20,
                        right: 20,
                        backgroundColor: '#000',
                        color: '#fff',
                        borderRadius: '15px',
                        padding: '5px 12px',
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: '#fff',
                            fontSize: 14,
                        }}>
                        Votes: {total_votes}
                    </Typography>
                </Box>
            )}

            {/* Project Image */}
            <CustomLazyLoadImage
                effect="blur"
                src={
                    project && project.media_type === 'picture'
                        ? project.feature_image
                        : project && project.thumbnail && isHover && shouldEnablePlayerProcessingState
                        ? project.thumbnail
                        : project.feature_image
                }
                alt="project_image"
                width={'100%'}
                onClick={() => {
                    if (project.media_type === 'picture') {
                        onPlay && onPlay(project);
                    }
                }}
                style={{
                    position: 'absolute',
                    zIndex: -1,
                    objectFit: isHover && shouldEnablePlayerProcessingState ? 'contain' : 'cover',
                    height: typeof height === 'string' ? height : `${height}px`,
                    borderRadius: '10px',
                }}
            />

            {/* Play Button */}
            {project.media_type !== 'picture' && (
                <EsPlayButton
                    onClick={() => onPlay && onPlay(project)}
                    disabled={!project.project && !project.processing_complete}
                />
            )}
        </Link>
    );
};

export { ProjectPlayer };
